import { graphql, Link, useStaticQuery } from 'gatsby';
import React, { ReactElement, useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import {
  Headline,
  Input,
  Link as StarkLink,
  Text,
} from '../../../../../components';
import IconDisclose from '../../../../icons/icon-disclose';
import IconSearch from '../../../../icons/icon-search';

// Shared by the nav's links and the Categories disclosure button. These used
// to be `.library-nav a:not(.library-nav__cta), .library-nav__categories-text`
// in style.css; putting them on the elements avoids a dozen arbitrary variants
// on the <nav>. 840px maps to the `nav` breakpoint.
const NAV_ITEM =
  'flex w-full cursor-pointer items-center border-none bg-none py-4 pr-0 pl-5 ' +
  'text-type-gray no-underline ' +
  'hover:text-base-purple-500 focus-visible:text-base-purple-500 active:text-base-purple-500 ' +
  '[&:hover_path]:stroke-base-purple-500 [&:focus_path]:stroke-base-purple-500 ' +
  '[&:active_path]:stroke-base-purple-500 ' +
  'max-nav:inline-flex max-nav:h-11 max-nav:w-auto max-nav:p-0';

// Applied by Gatsby's activeClassName. `library-nav--active` stays in the list
// as a hook for the mobile category grid, which zeroes the bottom border.
const NAV_ACTIVE =
  'library-nav--active relative text-base-purple-500! [&_path]:stroke-base-purple-500 hover:no-underline';

// The left side navigation that is present on all library pages.
// Also handles input changes to the search form.
export default function LibraryNav({
  onSearchInputChange,
  onSearch,
  onHideSearchResults,
}: any): ReactElement {
  const data = useStaticQuery(graphql`
    {
      allStrapiLibraryCategory {
        nodes {
          name
          slug
        }
      }
    }
  `);

  // Categories are handled via the /src/content/library-categories file.
  // Add a category there to have it show up here.
  const [showCategories, setShowCategories] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Putting this at the top because of limitations of gatsby's history
  // to not include search params.
  let searchParam;
  if (typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location.search);
    searchParam = urlParams.get('search');
  }

  useEffect(() => {
    const currentLoc = window.location.pathname;

    // They are on a category page so we should show them the list of
    // categories so they can easily switch between them
    if (
      currentLoc !== '/library/' &&
      !currentLoc.startsWith('/library/collections')
    ) {
      setShowCategories(true);
    }
  }, []);

  // This is necessary to ensure the form input shows the correct
  // search param
  useEffect(() => {
    if (searchParam) {
      setSearchQuery(searchParam);
    }
  }, [searchParam]);

  const handleCategoriesClick = () => {
    setShowCategories(!showCategories);
  };

  const handleInputChange = (e) => {
    setSearchQuery(e.target.value);
    onSearchInputChange(e.target.value);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    onSearch(searchQuery);
  };

  return (
    <nav
      className="w-70 min-w-70 pt-16 max-nav:mb-8 max-nav:w-full max-nav:min-w-full max-nav:border-b max-nav:border-neutral-gray-100 max-nav:pt-0"
      aria-label="Library category navigation">
      <div className="relative my-4 hidden items-center gap-8 max-nav:grid max-nav:grid-cols-3 max-[462px]:gap-4 max-[400px]:flex-wrap">
        <Link
          to="/library/"
          className={twMerge(NAV_ITEM, 'text-base leading-[1.7] font-semibold')}
          activeClassName={NAV_ACTIVE}
          onClick={() => onHideSearchResults()}>
          Library
        </Link>

        <Link
          to="/library/collections/"
          className={twMerge(NAV_ITEM, 'text-base leading-[1.7] font-semibold')}
          activeClassName={NAV_ACTIVE}
          partiallyActive
          onClick={() => onHideSearchResults()}>
          Collections
        </Link>

        <button
          className={twMerge(
            NAV_ITEM,
            'text-base leading-[1.7] font-semibold',
            showCategories &&
              'text-base-purple-500 hover:no-underline [&_path]:stroke-base-purple-500 [&>svg]:rotate-180'
          )}
          tabIndex={0}
          onClick={handleCategoriesClick}>
          <span className="flex items-center">Categories</span>

          <IconDisclose className="ml-5" />
        </button>

        {/*
          The label tweak is an arbitrary variant rather than a rule in
          style.css: now that Label emits utilities, a descendant selector in
          @layer components loses to them.
        */}
        <div className="relative text-form-input-border focus-within:text-form-input-active max-nav:col-[1/span_2] max-nav:w-full min-[401px]:max-nav:focus-within:absolute min-[401px]:max-nav:focus-within:right-0 min-[401px]:max-nav:focus-within:w-75 [&_.form-label]:mb-3.5 [&_.form-label]:leading-4">
          <IconSearch className="pointer-events-none absolute top-3 left-3" />

          <form onSubmit={handleSearchSubmit}>
            <Input
              value={searchQuery}
              minLength={3}
              required
              onChange={handleInputChange}
              className="pl-10"
            />
          </form>
        </div>
      </div>

      {showCategories && (
        <div className="hidden max-nav:mb-8 max-nav:grid max-nav:grid-cols-2 max-nav:gap-x-6 max-nav:gap-y-3 max-nav:rounded-sm max-nav:bg-neutral-gray-50 max-nav:p-4 max-nav:[&_.library-nav--active]:border-b-4 max-nav:[&_.library-nav--active]:border-transparent max-nav:[&>a]:h-auto">
          <Link
            key="all"
            to={`/library/all/`}
            className={twMerge(
              NAV_ITEM,
              'text-base leading-[1.7] font-semibold'
            )}
            activeClassName={NAV_ACTIVE}
            onClick={() => onHideSearchResults()}>
            All
          </Link>
          {data.allStrapiLibraryCategory.nodes.map(({ name, slug }) => (
            <Link
              key={name}
              to={`/library/${slug}/`}
              className={twMerge(
                NAV_ITEM,
                'text-base leading-[1.7] font-semibold'
              )}
              activeClassName={NAV_ACTIVE}
              onClick={() => onHideSearchResults()}>
              {name}
            </Link>
          ))}
        </div>
      )}

      <div id="libraryNav" className="scrollbar-subtle max-nav:hidden">
        <div className="relative mb-4 text-form-input-border focus-within:text-form-input-active [&_.form-label]:mb-3.5 [&_.form-label]:leading-4">
          <IconSearch className="pointer-events-none absolute top-10.5 left-3" />

          <form onSubmit={handleSearchSubmit}>
            <Input
              id="SearchLibraryInput"
              label="Search Library"
              value={searchQuery}
              minLength={3}
              required
              onChange={handleInputChange}
              className="pl-10"
            />
          </form>
        </div>

        <Link
          to="/library/"
          className={twMerge(NAV_ITEM, 'text-base leading-[1.7] font-semibold')}
          activeClassName={NAV_ACTIVE}
          onClick={() => onHideSearchResults()}>
          Home
        </Link>

        <Link
          to="/library/collections/"
          className={twMerge(NAV_ITEM, 'text-base leading-[1.7] font-semibold')}
          activeClassName={NAV_ACTIVE}
          partiallyActive
          onClick={() => onHideSearchResults()}>
          Collections
        </Link>

        <button
          className={twMerge(
            NAV_ITEM,
            'mb-2 text-base leading-[1.7] font-semibold',
            showCategories &&
              'text-base-purple-500 hover:no-underline [&_path]:stroke-base-purple-500 [&>svg]:rotate-180'
          )}
          tabIndex={0}
          onClick={handleCategoriesClick}>
          <span className="flex items-center">Categories</span>
          <IconDisclose className="mr-5 ml-auto" />
        </button>

        {showCategories && (
          <div className="[&>a]:pr-2 [&>a]:pl-12">
            <Link
              key="all"
              to={`/library/all/`}
              className={twMerge(
                NAV_ITEM,
                'text-base leading-[1.7] font-semibold'
              )}
              activeClassName={NAV_ACTIVE}
              onClick={() => onHideSearchResults()}>
              All
            </Link>
            {data.allStrapiLibraryCategory.nodes.map(({ name, slug }) => (
              <Link
                key={name}
                to={`/library/${slug}/`}
                className={twMerge(
                  NAV_ITEM,
                  'text-base leading-[1.7] font-semibold'
                )}
                activeClassName={NAV_ACTIVE}
                onClick={() => onHideSearchResults()}>
                {name}
              </Link>
            ))}
          </div>
        )}

        <div className="mt-6 rounded-lg bg-base-purple-100 p-6">
          <Headline level="4" className="text-type-blue">
            Contribute to Stark
          </Headline>

          <Text color="blue" className="mt-2 mb-6">
            Got any accessibility resources or news?
          </Text>

          <StarkLink
            style={{ minHeight: 44 }}
            linkType="button"
            target="_blank"
            rel="noopener noreferrer"
            href="https://forms.gle/V15UinvhcEePiJVm7"
            className="w-full pr-4 pl-4 max-nav:whitespace-nowrap">
            Submit a Resource
          </StarkLink>
        </div>
      </div>
    </nav>
  );
}
